import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Trash2, Plus, Minus, X } from 'lucide-react';
import { STORAGE_BASE } from '../config';
import { readUtm } from '../tenant';
import CustomerAccount from '../components/CustomerAccount';
import IdentityModal from '../components/IdentityModal';
import AddressModal from '../components/AddressModal';
import { downloadInvoicePdf } from '../utils/invoicePdf';

const MD = {
  primary: 'var(--primary)',
  onPrimary: 'var(--on-primary)',
  accent: 'var(--accent)',
  card: { background: 'var(--card)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' },
  onVariant: 'var(--muted-fg)',
  border: 'var(--border)',
  success: '#16A34A',
  successBg: 'var(--muted)',
  error: '#DC2626',
  btn: { width: '100%', padding: 13, borderRadius: 999, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.95rem' },
  btnOutline: { padding: '10px 18px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9rem' },
  input: { width: '100%', padding: 12, borderRadius: 12, border: '1.5px solid var(--border)', fontFamily: 'inherit', fontSize: '0.9rem', background: 'var(--card)', color: 'var(--fg)', outline: 'none', boxSizing: 'border-box' },
  sectionTitle: { margin: '0 0 4px', fontSize: '1rem', fontWeight: 800 },
  hint: { fontSize: '0.78rem', color: 'var(--muted-fg)', margin: '0 0 14px' },
  pick: (active) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 15px', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'right', width: '100%', boxSizing: 'border-box', color: 'var(--fg)', border: active ? '2px solid var(--primary)' : '1px solid var(--border)', background: active ? 'var(--muted)' : 'var(--card)' }),
  dot: (active) => ({ width: 18, height: 18, borderRadius: '50%', border: active ? '6px solid var(--primary)' : '2px solid var(--border)', flexShrink: 0, boxSizing: 'border-box' }),
  empty: { padding: 14, textAlign: 'center', color: 'var(--muted-fg)', fontSize: '0.82rem', border: '1px dashed var(--border)', borderRadius: 12, background: 'var(--card)' },
  row: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.92rem' },
};

const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('customer_token') || ''}` } });
const err = e => toast.error(e.response?.data?.message || 'خطا در انجام عملیات.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
const fa = n => Number(n || 0).toLocaleString('fa-IR');
const money = n => `${fa(Math.round(Number(n) || 0))} تومان`;
const dayLabel = iso => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('fa-IR', { weekday: 'long', month: 'long', day: 'numeric' });
};

export default function CartPage({ embedded, onDone }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  // shipping
  const [shipMethod, setShipMethod] = useState('shipping'); // 'shipping' | 'pickup'
  const [shipMethods, setShipMethods] = useState([]);       // admin courier methods
  const [shippingMethodId, setShippingMethodId] = useState('');
  const [pickupPoints, setPickupPoints] = useState([]);     // admin store addresses
  const [pickupPointId, setPickupPointId] = useState('');
  const [pickupDay, setPickupDay] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsClosed, setSlotsClosed] = useState(false);

  // customer addresses
  const [userAddresses, setUserAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState('');
  const [addrModalOpen, setAddrModalOpen] = useState(false);

  // invoice / payment
  const [discCode, setDiscCode] = useState('');
  const [disc, setDisc] = useState(null);
  const [feePct, setFeePct] = useState(0);
  const [taxPct, setTaxPct] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [gateways, setGateways] = useState([]);
  const [payMethod, setPayMethod] = useState('wallet');

  // identity verification gate (admin: site_settings.kyc.profile_required)
  const [kycRequired, setKycRequired] = useState(false);
  const [userNational, setUserNational] = useState('');
  const [idModalOpen, setIdModalOpen] = useState(false);

  const token = (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();

  const load = () => {
    if (!localStorage.getItem('customer_token')) { setLoading(false); return; }
    setLoading(true);
    axios.get('/cart', authH())
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(err)
      .finally(() => setLoading(false));
    axios.get('/account/addresses', authH())
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setUserAddresses(list);
        const def = list.find(a => a.is_default) || list[0];
        if (def) setSelectedAddrId(String(def.id));
      }).catch(() => {});
    axios.get('/account/summary', authH())
      .then(r => {
        setWalletBalance(Number(r.data?.user?.wallet_balance) || 0);
        setUserNational(r.data?.user?.national_code || '');
      })
      .catch(() => {});
    axios.get('/site-settings').then(r => {
      const d = r?.data || {};
      setFeePct(Number(d.fee) || 0);
      setTaxPct(Number(d.tax) || 0);
      setKycRequired(!!d.kyc?.profile_required);
      const cfg = d.shipping || {};
      setShipMethods((cfg.methods || []).filter(m => m && m.id && m.label));
      setPickupPoints((cfg.pickups || []).filter(p => p && p.id));
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    axios.get('/payment/gateways')
      .then(r => setGateways(Array.isArray(r.data) ? r.data.filter(g => g.id !== 'wallet') : []))
      .catch(() => {});
  }, []);

  // reset slot state when the store or day changes
  useEffect(() => { setPickupTime(''); }, [pickupPointId, pickupDay]);

  useEffect(() => {
    if (shipMethod !== 'pickup' || !pickupPointId || !pickupDay) { setSlots([]); setSlotsClosed(false); return; }
    setSlotsLoading(true);
    axios.get('/shipping/pickup-availability', { params: { point: pickupPointId, day: pickupDay } })
      .then(r => { setSlots(r.data?.slots || []); setSlotsClosed(!!r.data?.closed); })
      .catch(() => { setSlots([]); setSlotsClosed(false); })
      .finally(() => setSlotsLoading(false));
  }, [shipMethod, pickupPointId, pickupDay]);

  const subtotal = items.reduce((a, it) => {
    const unit = Number(it.unit_price) || it.product?.customization_data?.base_price || it.product?.price || 0;
    return a + unit * (it.qty || 1);
  }, 0);
  const discAmount = disc ? disc.amount : 0;

  const selectedShipMethod = shipMethods.find(m => m.id === shippingMethodId) || null;
  const shipIsPostpaid = !!(selectedShipMethod && selectedShipMethod.postpaid);
  const shippingCost = (shipMethod === 'pickup' || shipIsPostpaid) ? 0 : Number(selectedShipMethod?.cost || 0);

  const afterDisc = Math.max(0, subtotal - discAmount);
  const feeAmount = Math.round(afterDisc * feePct) / 100;
  const taxAmount = Math.round((afterDisc + feeAmount) * taxPct) / 100;
  const payable = Math.max(0, afterDisc + feeAmount + taxAmount + shippingCost);

  // approximate delivery date for the courier method (admin-defined days)
  const deliveryEta = (() => {
    if (shipMethod !== 'shipping' || !selectedShipMethod) return null;
    const days = Number(selectedShipMethod.delivery_days || 0);
    if (!days) return selectedShipMethod.delivery_time || null;
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString('fa-IR', { weekday: 'long', month: 'long', day: 'numeric' });
  })();

  const pickupPoint = pickupPoints.find(p => p.id === pickupPointId) || null;
  const isClosedDay = iso => {
    if (!pickupPoint) return false;
    if (pickupPoint.weekly_holiday) {
      const wd = new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      if (wd === String(pickupPoint.weekly_holiday).toLowerCase()) return true;
    }
    return (pickupPoint.holidays || []).includes(iso);
  };

  const setQty = async (it, qty) => {
    if (qty < 1) return;
    setBusy(true);
    try {
      await axios.put(`/cart/${it.id}`, { qty }, authH());
      setItems(items.map(x => x.id === it.id ? { ...x, qty } : x));
      window.dispatchEvent(new Event('cart-updated'));
    } catch (e) { err(e); load(); }
    setBusy(false);
  };

  const remove = async id => {
    try {
      await axios.delete(`/cart/${id}`, authH());
      setItems(items.filter(x => x.id !== id));
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('از سبد حذف شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) { err(e); }
  };

  const applyDiscount = async () => {
    const code = discCode.trim();
    if (!code) { toast.error('کد تخفیف را وارد کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); return; }
    setBusy(true);
    try {
      const r = await axios.post('/discount/validate', { code, subtotal, purpose: 'invoice' }, authH());
      setDisc({ code: r.data.code, amount: Number(r.data.amount) || 0 });
      toast.success(`تخفیف اعمال شد: ${fa(r.data.amount)} تومان`, { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) { setDisc(null); err(e); }
    setBusy(false);
  };

  const checkout = async (skipIdentity = false) => {
    if (!items.length) return;
    // identity gate: if admin made KYC mandatory, ask once via modal, then continue
    if (!skipIdentity && kycRequired && !userNational) { setIdModalOpen(true); return; }
    setBusy(true);

    // --- shipping validation ---
    if (shipMethod === 'pickup') {
      if (!pickupPointId) { toast.error('آدرس فروشگاه را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); setBusy(false); return; }
      if (!pickupDay) { toast.error('روز مراجعه را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); setBusy(false); return; }
      if (!pickupTime) { toast.error('بازه ساعتی مراجعه را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); setBusy(false); return; }
    } else {
      if (!shippingMethodId) { toast.error('روش ارسال را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); setBusy(false); return; }
      if (!selectedAddrId) { toast.error('آدرس تحویل را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); setBusy(false); return; }
    }

    // --- payment validation ---
    if (payMethod === 'wallet' && walletBalance < payable) {
      toast.error(`موجودی کیف پول کافی نیست (${money(walletBalance)}). پرداخت ترکیبی را انتخاب کنید.`, { position: 'bottom-right', autoClose: 6000, theme: 'colored' });
      setBusy(false);
      return;
    }

    const addrRow = userAddresses.find(a => String(a.id) === String(selectedAddrId)) || null;

    try {
      const orderRes = await axios.post('/orders/checkout', {
        customer_token: localStorage.getItem('customer_token') || '',
        items: items.map(it => ({
          product_id: it.product_id, qty: it.qty,
          customizations: it.customizations || {}, attributes: it.attributes || {},
        })),
        shipping_method: shipMethod,
        shipping_method_id: shipMethod === 'shipping' ? shippingMethodId : undefined,
        pickup_point_id: shipMethod === 'pickup' ? pickupPointId : undefined,
        pickup_day: shipMethod === 'pickup' ? pickupDay : undefined,
        pickup_time: shipMethod === 'pickup' ? pickupTime : undefined,
        address_id: shipMethod === 'shipping' && selectedAddrId ? (parseInt(selectedAddrId, 10) || null) : null,
        shipping_address: shipMethod === 'shipping' ? (addrRow?.address || '') : undefined,
        shipping_province: shipMethod === 'shipping' ? (addrRow?.province || '') : undefined,
        shipping_city: shipMethod === 'shipping' ? (addrRow?.city || '') : undefined,
        discount_code: disc ? disc.code : undefined,
        fee_percent: 0,
        tax_percent: 0,
        ...readUtm(),
      }, authH());

      const orderId = orderRes.data.id;
      const orderTotal = Number(orderRes.data.payable ?? orderRes.data.price ?? payable);

      if (payMethod === 'wallet') {
        await axios.post('/payment/wallet', { order_id: orderId }, authH());
      } else if (payMethod === 'split') {
        const r = await axios.post('/payment/wallet-partial', { order_id: orderId, gateway: gateways[0]?.id }, authH());
        if (r.data?.url) { window.location.href = r.data.url; return; }
        toast.error('آدرس درگاه دریافت نشد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
        setBusy(false);
        return;
      } else {
        const payRes = await axios.post('/payment/request', { order_id: orderId, gateway: payMethod }, authH());
        if (payRes.data?.url) { window.location.href = payRes.data.url; return; }
        toast.error('آدرس درگاه دریافت نشد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
        setBusy(false);
        return;
      }

      setDone({
        id: orderId,
        total: payable,
        subtotal,
        discount: discAmount,
        discountCode: disc?.code || '',
        fee: feeAmount,
        feePercent: feePct,
        tax: taxAmount,
        taxPercent: taxPct,
        shippingCost,
        shippingMethod: shipMethod === 'pickup' ? 'تحویل حضوری' : (selectedShipMethod?.name || 'پست'),
        gateway: payMethod,
        items: items.map(it => `${it.product?.name || ''} × ${it.qty || 1}`),
        phone: localStorage.getItem('user_phone') || localStorage.getItem('customer_phone') || '',
        date: new Date().toLocaleDateString('fa-IR'),
      });
      setItems([]);
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('پرداخت انجام شد! کد پیگیری: ' + orderId, { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) {
      if (e.response?.data?.need_identity) { setIdModalOpen(true); }
      else err(e);
    }
    setBusy(false);
  };

  if (!token) {
    return (
      <div style={embedded ? { direction: 'rtl' } : { padding: '40px 16px', direction: 'rtl', maxWidth: 560 }}>
        {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
        {embedded ? (
          <>
            <p style={{ color: MD.onVariant, fontSize: '0.9rem', margin: '0 0 16px', textAlign: 'center' }}>برای مشاهده سبد خرید ابتدا وارد شوید.</p>
            <CustomerAccount compact onDone={() => window.location.reload()} />
          </>
        ) : (
          <div style={{ ...MD.card, padding: 28, textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', fontWeight: 800 }}>سبد خرید</h2>
            <p style={{ color: MD.onVariant, fontSize: '0.9rem', margin: '0 0 16px' }}>برای مشاهده سبد خرید ابتدا وارد شوید.</p>
            <CustomerAccount compact onDone={() => window.location.reload()} />
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={embedded ? { direction: 'rtl', padding: 12, textAlign: 'center' } : { padding: '40px 16px', direction: 'rtl', maxWidth: 960 }}>
        {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
        <div style={{ ...MD.card, padding: 40, textAlign: 'center', color: MD.onVariant }}>در حال بارگذاری...</div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={embedded ? { direction: 'rtl' } : { padding: '40px 16px', direction: 'rtl', maxWidth: 640 }}>
        {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
        <div style={{ ...MD.card, padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: MD.successBg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '1.8rem', fontWeight: 800, color: MD.success }}>✓</div>
          <h2 style={{ margin: '0 0 6px', fontSize: '1.15rem', fontWeight: 800 }}>سفارش ثبت و پرداخت شد</h2>
          <p style={{ color: MD.onVariant, fontSize: '0.9rem', margin: '0 0 18px' }}>کد پیگیری: <b>{done.id}</b> — مبلغ: <b>{money(done.total)}</b></p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => downloadInvoicePdf({
              orderId: done.id,
              productName: (done.items || []).join('، ') || '—',
              subtotal: done.subtotal ?? done.total,
              total: done.total,
              discount: done.discount ?? 0,
              discountCode: done.discountCode || '',
              fee: done.fee ?? 0,
              feePercent: done.feePercent ?? 0,
              tax: done.tax ?? 0,
              taxPercent: done.taxPercent ?? 0,
              shippingMethod: done.shippingMethod || '',
              shippingCost: done.shippingCost ?? 0,
              phone: done.phone || '',
              date: done.date || '',
              gatewayLabel: done.gateway === 'wallet' ? 'کیف پول' : done.gateway === 'split' ? 'کیف پول + درگاه' : 'درگاه آنلاین',
            })} style={MD.btnOutline}>دانلود فاکتور</button>
            <button onClick={() => navigate('/products')} style={{ ...MD.btn, width: 'auto', padding: '10px 26px' }}>ادامه خرید</button>
          </div>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div style={embedded ? { direction: 'rtl' } : { padding: '40px 16px', direction: 'rtl', maxWidth: 640 }}>
        {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
        <div style={{ ...MD.card, padding: 40, textAlign: 'center' }}>
          <p style={{ color: MD.onVariant, margin: '0 0 16px' }}>سبد خرید شما خالی است.</p>
          <Link to="/products" className="btn btn-dark">مشاهده محصولات</Link>
        </div>
      </div>
    );
  }

  if (embedded) {
    const goCart = () => { if (onDone) onDone(); navigate('/cart'); };
    return (
      <div style={{ direction: 'rtl' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(it => {
            const p = it.product || {};
            const unit = Number(it.unit_price) || p.customization_data?.base_price || p.price || 0;
            const src = p.image_path ? `${STORAGE_BASE}/${String(p.image_path).replace(/^\/+/, '')}` : 'https://via.placeholder.com/200x200?text=Silver';
            return (
              <div key={it.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <Link to={`/products/${p.id}`} onClick={() => onDone && onDone()}><img src={src} alt={p.name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 12, background: 'var(--muted)', display: 'block' }} /></Link>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/products/${p.id}`} onClick={() => onDone && onDone()} style={{ fontWeight: 700, color: 'var(--fg)', textDecoration: 'none', fontSize: '0.88rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</Link>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted-fg)', marginTop: 2 }}>{fa(it.qty || 1)} × {money(unit)}</div>
                </div>
                <button onClick={() => remove(it.id)} aria-label="حذف" style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--muted-fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0', fontWeight: 800 }}>
          <span>جمع اقلام</span><span>{money(subtotal)}</span>
        </div>
        <button onClick={goCart} style={{ width: '100%', padding: 13, borderRadius: 12, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'inherit', fontSize: '0.92rem', fontWeight: 800, cursor: 'pointer' }}>تکمیل خرید</button>
      </div>
    );
  }

  return (
    <div style={embedded ? { direction: 'rtl' } : { padding: '28px 16px 60px', direction: 'rtl', maxWidth: 960 }}>
      {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
      {!embedded && <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 18px' }}>سبد خرید</h1>}

      {/* ---------- items ---------- */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
        {items.map(it => {
          const p = it.product || {};
          const cust = Object.entries(it.customizations || {}).filter(([, v]) => String(v ?? '').trim() !== '');
          const src = p.image_path ? `${STORAGE_BASE}/${String(p.image_path).replace(/^\/\/+/, '')}` : 'https://via.placeholder.com/200x200?text=Silver';
          return (
            <div key={it.id} style={{ ...MD.card, padding: 14, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <Link to={`/products/${p.id}`}><img src={src} alt={p.name} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 12, background: 'var(--muted)' }} /></Link>
              <div style={{ flex: 1, minWidth: 180 }}>
                <Link to={`/products/${p.id}`} style={{ fontWeight: 700, color: MD.primary, textDecoration: 'none', fontSize: '0.95rem' }}>{p.name}</Link>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, marginTop: 4 }}>{money(Number(it.unit_price) || p.customization_data?.base_price || p.price || 0)}</div>
                {cust.length > 0 && (
                  <div style={{ fontSize: '0.76rem', color: MD.success, marginTop: 6, background: MD.successBg, borderRadius: 8, padding: '5px 10px', lineHeight: 1.7 }}>
                    {cust.map(([k, v], i) => <span key={k}>{k}: {String(v).trim()}{i < cust.length - 1 ? ' · ' : ''}</span>)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setQty(it, (it.qty || 1) - 1)} disabled={busy} style={{ ...MD.btnOutline, padding: '6px 10px' }}><Minus size={15} /></button>
                <b style={{ minWidth: 28, textAlign: 'center' }}>{fa(it.qty)}</b>
                <button onClick={() => setQty(it, (it.qty || 1) + 1)} disabled={busy} style={{ ...MD.btnOutline, padding: '6px 10px' }}><Plus size={15} /></button>
              </div>
              <button onClick={() => remove(it.id)} style={{ ...MD.btnOutline, borderColor: 'var(--border)', color: MD.error, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={15} /> حذف
              </button>
            </div>
          );
        })}
      </div>

      {/* ---------- shipping ---------- */}
      <div style={{ ...MD.card, padding: 18, marginTop: 16 }}>
        <h3 style={MD.sectionTitle}>روش دریافت سفارش</h3>
        <p style={MD.hint}>ابتدا مشخص کنید سفارش را حضوری تحویل می‌گیرید یا پستی برایتان ارسال شود.</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <button type="button" onClick={() => setShipMethod('pickup')} style={{ ...MD.pick(shipMethod === 'pickup'), flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <span style={{ fontWeight: 800, fontSize: '0.92rem' }}>تحویل حضوری</span>
            <span style={{ fontSize: '0.75rem', color: MD.onVariant }}>مراجعه به آدرس فروشگاه — بدون هزینه ارسال</span>
          </button>
          <button type="button" onClick={() => setShipMethod('shipping')} style={{ ...MD.pick(shipMethod === 'shipping'), flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <span style={{ fontWeight: 800, fontSize: '0.92rem' }}>ارسال پستی</span>
            <span style={{ fontSize: '0.75rem', color: MD.onVariant }}>ارسال به آدرس شما با هزینه ارسال</span>
          </button>
        </div>

        {shipMethod === 'shipping' ? (
          <>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>روش ارسال را انتخاب کنید</label>
            {shipMethods.length === 0 ? (
              <div style={MD.empty}>روش ارسالی توسط فروشگاه ثبت نشده است.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {shipMethods.map(m => {
                  const active = shippingMethodId === m.id;
                  return (
                    <button key={m.id} onClick={() => setShippingMethodId(m.id)} style={MD.pick(active)}>
                      <span style={MD.dot(active)} />
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem' }}>{m.label}</span>
                        {m.desc && <span style={{ display: 'block', fontSize: '0.76rem', color: MD.onVariant }}>{m.desc}</span>}
                        {m.delivery_time && <span style={{ display: 'block', fontSize: '0.76rem', color: MD.accent, fontWeight: 600 }}>زمان تحویل: {m.delivery_time}</span>}
                      </span>
                      <span style={{ fontWeight: 800, fontSize: '0.9rem', color: m.postpaid || !Number(m.cost) ? MD.success : MD.primary }}>
                        {m.postpaid ? 'پرداخت هنگام تحویل' : (Number(m.cost) ? money(m.cost) : 'رایگان')}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>آدرس تحویل</label>
            {userAddresses.length === 0 ? (
              <div style={MD.empty}>هنوز آدرسی ثبت نکرده‌اید — با دکمه زیر آدرس جدید بسازید.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {userAddresses.map(a => {
                  const active = String(selectedAddrId) === String(a.id);
                  return (
                    <button key={a.id} onClick={() => setSelectedAddrId(String(a.id))} style={MD.pick(active)}>
                      <span style={MD.dot(active)} />
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'block', fontWeight: 700, fontSize: '0.9rem' }}>{a.address || 'بدون عنوان'}</span>
                        <span style={{ display: 'block', fontSize: '0.76rem', color: MD.onVariant }}>
                          {[a.province, a.city].filter(Boolean).join(' · ')}{a.postal_code ? ` · کدپستی ${a.postal_code}` : ''}
                        </span>
                      </span>
                      {a.is_default && <span style={{ fontSize: '0.62rem', background: MD.primary, color: MD.onPrimary, padding: '2px 7px', borderRadius: 6, fontWeight: 700 }}>پیش‌فرض</span>}
                    </button>
                  );
                })}
              </div>
            )}

            <button onClick={() => setAddrModalOpen(true)} style={{ ...MD.btnOutline, marginTop: 10 }}>افزودن آدرس جدید</button>

            {deliveryEta && (
              <div style={{ marginTop: 14, background: MD.successBg, color: MD.success, borderRadius: 12, padding: '11px 14px', fontSize: '0.84rem', fontWeight: 700 }}>
                روز تقریبی دریافت سفارش: {deliveryEta}
              </div>
            )}
          </>
        ) : (
          <>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>آدرس فروشگاه را انتخاب کنید</label>
            {pickupPoints.length === 0 ? (
              <div style={MD.empty}>آدرسی توسط فروشگاه ثبت نشده است.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {pickupPoints.map(p => {
                  const active = pickupPointId === p.id;
                  return (
                    <button key={p.id} onClick={() => { setPickupPointId(p.id); setPickupDay(''); }} style={MD.pick(active)}>
                      <span style={MD.dot(active)} />
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem' }}>{p.label || 'فروشگاه'}</span>
                        <span style={{ display: 'block', fontSize: '0.76rem', color: MD.onVariant }}>
                          {[p.province, p.city, p.address].filter(Boolean).join(' · ')}
                        </span>
                        {(p.hours_from || p.hours_to) && (
                          <span style={{ display: 'block', fontSize: '0.74rem', color: MD.accent, fontWeight: 600 }}>
                            ساعات کاری: {p.hours_from} تا {p.hours_to}{p.weekly_holiday_label ? ` — تعطیل ${p.weekly_holiday_label}` : ''}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {pickupPointId && (
              <>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>روز مراجعه (۷ روز آینده)</label>
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 16 }}>
                  {Array.from({ length: 7 }, (_, i) => {
                    const d = new Date(); d.setDate(d.getDate() + i);
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    const closed = isClosedDay(iso);
                    const active = pickupDay === iso;
                    return (
                      <button key={iso} type="button" disabled={closed} onClick={() => setPickupDay(iso)}
                        style={{ flexShrink: 0, minWidth: 78, padding: '10px 8px', borderRadius: 12, fontFamily: 'inherit', cursor: closed ? 'not-allowed' : 'pointer', border: active ? '2px solid var(--primary)' : '1px solid var(--border)', background: closed ? 'var(--muted)' : (active ? 'var(--muted)' : 'var(--card)'), color: closed ? 'var(--muted-fg)' : MD.primary }}>
                        <span style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700 }}>{d.toLocaleDateString('fa-IR', { weekday: 'long' })}</span>
                        <span style={{ display: 'block', fontSize: '0.72rem', color: closed ? 'var(--muted-fg)' : MD.onVariant }}>{d.toLocaleDateString('fa-IR', { month: 'long', day: 'numeric' })}</span>
                        {closed && <span style={{ display: 'block', fontSize: '0.62rem', color: MD.error }}>تعطیل</span>}
                      </button>
                    );
                  })}
                </div>

                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>بازه ساعتی مراجعه (هر بازه ۲ ساعت)</label>
                {!pickupDay ? (
                  <div style={MD.empty}>ابتدا روز مراجعه را انتخاب کنید.</div>
                ) : slotsLoading ? (
                  <div style={MD.empty}>در حال بررسی ظرفیت...</div>
                ) : slotsClosed ? (
                  <div style={{ ...MD.empty, color: MD.error, borderColor: 'var(--border)', background: 'var(--card)' }}>فروشگاه در این روز تعطیل است.</div>
                ) : slots.length === 0 ? (
                  <div style={MD.empty}>بازه زمانی برای این روز ثبت نشده است.</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                    {slots.map(s => {
                      const full = Number(s.available) <= 0;
                      const active = pickupTime === s.time;
                      return (
                        <button key={s.time} type="button" disabled={full} onClick={() => setPickupTime(s.time)}
                          style={{ padding: '10px 12px', borderRadius: 12, fontFamily: 'inherit', textAlign: 'center', cursor: full ? 'not-allowed' : 'pointer', border: active ? '2px solid var(--primary)' : '1px solid var(--border)', background: full ? 'var(--muted)' : (active ? 'var(--muted)' : 'var(--card)'), color: full ? 'var(--muted-fg)' : MD.primary }}>
                          <span style={{ display: 'block', fontWeight: 700, fontSize: '0.84rem' }}>{s.label}</span>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: full ? 'var(--muted-fg)' : MD.success }}>
                            {full ? 'تکمیل' : `${fa(s.available)} ظرفیت باقی‌مانده`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {pickupDay && pickupTime && (
                  <div style={{ marginTop: 14, background: MD.successBg, color: MD.success, borderRadius: 12, padding: '11px 14px', fontSize: '0.84rem', fontWeight: 700 }}>
                    زمان تحویل حضوری: {dayLabel(pickupDay)} — ساعت {pickupTime}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ---------- invoice ---------- */}
      <div style={{ ...MD.card, padding: 18, marginTop: 16 }}>
        <h3 style={MD.sectionTitle}>فاکتور</h3>
        <p style={MD.hint}>مبالغ نهایی هنگام ثبت سفارش در سرور محاسبه می‌شود.</p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input value={discCode} onChange={e => setDiscCode(e.target.value.toUpperCase())} placeholder="کد تخفیف (اختیاری)" dir="ltr" style={{ ...MD.input, textAlign: 'center', fontWeight: 700, letterSpacing: 1 }} />
          <button onClick={applyDiscount} disabled={busy} style={{ ...MD.btn, width: 'auto', padding: '10px 22px' }}>اعمال</button>
        </div>

        <div style={MD.row}><span style={{ color: MD.onVariant }}>جمع اقلام</span><b>{money(subtotal)}</b></div>
        {discAmount > 0 && (
          <div style={MD.row}><span style={{ color: MD.success }}>تخفیف {disc?.code ? `(${disc.code})` : ''}</span><b style={{ color: MD.success }}>−{money(discAmount)}</b></div>
        )}
        {feeAmount > 0 && <div style={MD.row}><span style={{ color: MD.onVariant }}>کارمزد ({fa(feePct)}٪)</span><b>{money(feeAmount)}</b></div>}
        {taxAmount > 0 && <div style={MD.row}><span style={{ color: MD.onVariant }}>مالیات ({fa(taxPct)}٪)</span><b>{money(taxAmount)}</b></div>}
        <div style={MD.row}>
          <span style={{ color: MD.onVariant }}>هزینه ارسال</span>
          <b style={{ color: shippingCost ? MD.primary : MD.success }}>
            {shipMethod === 'pickup' ? 'حضوری — بدون هزینه' : shipIsPostpaid ? 'پرداخت هنگام تحویل' : (shippingCost ? money(shippingCost) : 'رایگان')}
          </b>
        </div>
        <div style={{ ...MD.row, padding: '10px 0', marginTop: 6, borderTop: `1px solid ${MD.border}`, fontWeight: 800, fontSize: '1.05rem' }}>
          <span>قابل پرداخت</span><span>{money(payable)}</span>
        </div>
      </div>

      {/* ---------- payment ---------- */}
      <div style={{ ...MD.card, padding: 18, marginTop: 16 }}>
        <h3 style={MD.sectionTitle}>روش پرداخت</h3>
        <p style={MD.hint}>موجودی کیف پول شما: <b>{money(walletBalance)}</b></p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setPayMethod('wallet')} disabled={walletBalance < payable} style={{ ...MD.pick(payMethod === 'wallet'), opacity: walletBalance < payable ? 0.55 : 1, cursor: walletBalance < payable ? 'not-allowed' : 'pointer' }}>
            <span style={MD.dot(payMethod === 'wallet')} />
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem' }}>پرداخت کامل با کیف پول</span>
              <span style={{ display: 'block', fontSize: '0.76rem', color: MD.onVariant }}>
                {walletBalance >= payable ? 'موجودی کافی است' : `موجودی کافی نیست — کمبود ${money(payable - walletBalance)}`}
              </span>
            </span>
          </button>

          {gateways.map(g => (
            <button key={g.id} onClick={() => setPayMethod(g.id)} style={MD.pick(payMethod === g.id)}>
              <span style={MD.dot(payMethod === g.id)} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem' }}>{g.label}</span>
                {g.desc && <span style={{ display: 'block', fontSize: '0.76rem', color: MD.onVariant }}>{g.desc}</span>}
              </span>
            </button>
          ))}

          {walletBalance > 0 && walletBalance < payable && (
            <button onClick={() => setPayMethod('split')} style={MD.pick(payMethod === 'split')}>
              <span style={MD.dot(payMethod === 'split')} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem' }}>پرداخت ترکیبی (کیف پول + درگاه)</span>
                <span style={{ display: 'block', fontSize: '0.76rem', color: MD.onVariant }}>
                  {money(walletBalance)} از کیف پول، {money(payable - walletBalance)} از درگاه آنلاین
                </span>
              </span>
            </button>
          )}
        </div>

        <button onClick={checkout} disabled={busy} style={{ ...MD.btn, opacity: busy ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {busy && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'var(--on-primary)', borderRadius: '50%', display: 'inline-block', animation: 'hb-spin 0.6s linear infinite' }} />}
          {busy ? 'در حال پردازش...' : (payMethod === 'wallet' ? 'پرداخت از کیف پول' : payMethod === 'split' ? 'پرداخت ترکیبی' : 'تکمیل خرید و پرداخت')}
        </button>
      </div>

      {/* ---------- add address modal ---------- */}
      {addrModalOpen && (
        <AddressModal
          open={addrModalOpen}
          onClose={() => setAddrModalOpen(false)}
          title="افزودن آدرس جدید"
          initial={{ address: '', province: '', city: '', postal_code: '', is_default: false }}
          submitLabel="ذخیره"
          busy={busy}
          onSubmit={async (f) => {
            if ((f.address || '').trim().length < 10) {
              toast.error('آدرس کامل را وارد کنید (حداقل ۱۰ حرف).', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
              return;
            }
            setBusy(true);
            try {
              const r = await axios.post('/account/addresses', f, authH());
              setAddrModalOpen(false);
              load();
              if (r.data?.id) setSelectedAddrId(String(r.data.id));
              toast.success('آدرس ذخیره شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
            } catch (e) { err(e); }
            setBusy(false);
          }}
        />
      )}
      <IdentityModal open={idModalOpen} inline={embedded} onClose={() => setIdModalOpen(false)}
        note="برای ثبت سفارش و پرداخت، ابتدا احراز هویت (کد ملی و تاریخ تولد) را تکمیل کنید."
        onVerified={u => { setUserNational(u?.national_code || ''); setIdModalOpen(false); checkout(true); }} />
    </div>
  );
}
